import { checkSession } from "@features/auth";
import { getUserOrganizationId } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Badge } from "@shadcn/ui/badge";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { ArrowLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { z } from "zod";
import { formatConfigurationLabel, formatDimensions, getSizeBucket, type SizeBucket } from "~/components/lock/spaceConfiguration";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { SizeTabs } from "~/components/warenannahme/SizeTabs";
import { DeliveryRequestList } from "~/components/warenannahme/DeliveryRequestList";
import { useHubGate } from "~/components/warenannahme/useHubGate";
import type { warenannahmeDeliverQuery } from "./__generated__/warenannahmeDeliverQuery.graphql";

export const query = graphql`
	query warenannahmeDeliverQuery($hubName: String!, $organizationId: UUID!) @relay_test_operation {
		deliveryRequests(filter: { direction: RECEIVED, status: [OPEN] }, pagination: { page: 0, pageSize: 100 }) {
			nodes {
				id
				hubId
				createdAt
				content
				preferredSize
				requesterOrganization {
					id
					name
				}
			}
		}
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			lat
			lng
			organizationCapacity(organizationId: $organizationId) {
				fixedAvailable
			}
			capacityOverview {
				flexCapacityAvailable
			}
			hub @required(action: THROW) {
				id
				status
				spaces {
					frame
				}
				availableConfigurations {
					numSpaces
					configuration
					dimensions {
						widthMm
						heightMm
						depthMm
					}
					spaces {
						id
						frame
					}
				}
			}
		}
	}
`;

type FreeSpace = warenannahmeDeliverQuery["response"]["hubMeta"]["hub"]["availableConfigurations"][number]["spaces"][number];

/** A free compartment on the side of the hub the user is at, else any free one. */
function spaceOnSide(spaces: ReadonlyArray<FreeSpace>, side: "A" | "B" | undefined): FreeSpace | undefined {
	return spaces.find((space) => side && space.frame.startsWith(side)) ?? spaces[0];
}

export const Route = createFileRoute("/$hubMetaName/warenannahme/deliver/")({
	beforeLoad: () => {
		document.title = "bacehub | Warenannahme";
	},
	component: RouteComponent,
	validateSearch: zodValidator(z.object({ requestId: z.string().optional(), start: z.boolean().optional() })),
	loaderDeps: ({ search }) => ({ requestId: search.requestId }),
	loader: async (ctx) => {
		if (!(await checkSession())) {
			const query = ctx.deps.requestId ? `?requestId=${encodeURIComponent(ctx.deps.requestId)}` : "";
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/warenannahme/deliver${query}` },
			});
		}
		const organizationId = getUserOrganizationId();
		if (!organizationId) {
			throw redirect({ to: "/$hubMetaName/warenannahme/action", params: { hubMetaName: ctx.params.hubMetaName } });
		}
		return preloadQuery<warenannahmeDeliverQuery>(ctx.context.relayEnvironment, query, {
			hubName: ctx.params.hubMetaName,
			organizationId,
		});
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const i18n = useI18nContext().LL["new-ui"];
	const { hubMeta, deliveryRequests } = usePreloadedQuery<warenannahmeDeliverQuery>(query, Route.useLoaderData());
	const { requestId, start } = Route.useSearch();
	const requests = deliveryRequests.nodes.filter((request) => request.hubId === hubMeta.hub.id);
	const selectedRequest = requests.find((request) => request.id === requestId);
	const isHubOnline = hubMeta.hub.status === "ONLINE";
	const hubGate = useHubGate({
		id: hubMeta.hub.id,
		name: hubMeta.name,
		lat: hubMeta.lat,
		lng: hubMeta.lng,
		frames: hubMeta.hub.spaces.map((space) => space.frame),
	});

	// Free compartments come out of the organization's own (fixed) compartments first, then are
	// booked from the hub's shared pool, like deliveries in the dashboard. The driver does not see the difference.
	const ownFree = Math.max(hubMeta.organizationCapacity.fixedAvailable, 0);
	const onDemandFree = Math.max(hubMeta.capacityOverview.flexCapacityAvailable, 0);
	const bookable = ownFree + onDemandFree;
	const freeConfigurations = hubMeta.hub.availableConfigurations.filter((config) => config.numSpaces > 0 && config.spaces.length > 0);
	const configurations = bookable > 0 ? freeConfigurations : [];
	const counts = new Map<SizeBucket, number>();
	for (const config of configurations) {
		const size = getSizeBucket(config.configuration);
		if (size) counts.set(size, Math.min((counts.get(size) ?? 0) + config.numSpaces, bookable));
	}
	const preferredSize = (size: string | null | undefined) => (size && counts.has(size as SizeBucket) ? (size as SizeBucket) : undefined);
	const [sizeFilter, setSizeFilter] = useState<SizeBucket | undefined>(
		() => preferredSize(selectedRequest?.preferredSize) ?? [...counts.keys()][0]
	);
	const visibleConfigurations = sizeFilter
		? configurations.filter((config) => getSizeBucket(config.configuration) === sizeFilter)
		: configurations;
	const [unavailableSize, setUnavailableSize] = useState<string | null>(null);

	const deliverInto = (spaces: ReadonlyArray<FreeSpace>, request: DeliveryRequest | undefined) =>
		hubGate.run((side) => {
			const space = spaceOnSide(spaces, side);
			if (!space) return;
			navigate({
				to: "/$hubMetaName/warenannahme/deliver/$spaceId",
				params: { hubMetaName: hubMeta.name, spaceId: space.id },
				search: request ? { requestId: request.id } : {},
			});
		});

	const showRequest = (request: DeliveryRequest | undefined) =>
		navigate({
			to: "/$hubMetaName/warenannahme/deliver",
			params: { hubMetaName: hubMeta.name },
			search: request ? { requestId: request.id } : {},
			replace: true,
		});

	// Delivering for a request goes straight to a free compartment of the preferred size (or any
	// free one without a preference). If that size is taken, the driver chooses another one.
	const deliverRequest = (request: DeliveryRequest | undefined) => {
		setUnavailableSize(null);
		void showRequest(request);
		if (!request) return;
		const size = request.preferredSize ? preferredSize(request.preferredSize) : [...counts.keys()][0];
		if (!size) {
			if (request.preferredSize) setUnavailableSize(request.preferredSize);
			return;
		}
		setSizeFilter(size);
		if (!isHubOnline) return;
		const spaces = configurations.filter((config) => getSizeBucket(config.configuration) === size).flatMap((config) => config.spaces);
		deliverInto(spaces, request);
	};

	// "Deliver this" on the overview: start delivering for the request as soon as the page opens.
	const started = useRef(false);
	useEffect(() => {
		if (!start || started.current) return;
		started.current = true;
		deliverRequest(selectedRequest);
	});

	return (
		<PageContainer>
			<AppHeader hubMetaName={hubMeta.name} service="LOCK_B2B" />
			<div className="flex flex-col gap-6 px-6 pt-6">
				<h1 className="text-2xl font-breit font-medium">
					<T>{i18n.WA_SELECT_TITLE()}</T>
				</h1>
				{!isHubOnline ? (
					<p role="status" className="rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground">
						<T>{i18n.WA_HUB_OFFLINE()}</T>
					</p>
				) : null}

				{requestId && !selectedRequest ? (
					<p role="status" className="rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground">
						<T>{i18n.WA_REQUEST_NOT_OPEN()}</T>
					</p>
				) : null}

				{/* "Ask to deliver" is hidden for now.
				{requests.length > 0 ? (
					<DeliveryRequestList requests={requests} selectedId={selectedRequest?.id} onSelect={deliverRequest} />
				) : null}
				*/}

				{unavailableSize && selectedRequest ? (
					<p role="status" className="rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground">
						<T>{i18n.WA_REQUEST_SIZE_UNAVAILABLE({ size: unavailableSize })}</T>
					</p>
				) : null}

				{configurations.length === 0 ? (
					<>
						<p className="text-sm text-muted-foreground">
							<T>{freeConfigurations.length > 0 ? i18n.WA_SELECT_CAPACITY_EXHAUSTED() : i18n.WA_SELECT_EMPTY_DELIVER()}</T>
						</p>
						<Button
							variant="outline"
							className="w-full"
							onClick={() => navigate({ to: "/$hubMetaName/warenannahme", params: { hubMetaName: hubMeta.name } })}
						>
							<ArrowLeft className="size-4" />
							<T>{i18n.WA_BACK_TO_OVERVIEW()}</T>
						</Button>
					</>
				) : (
					<>
						<SizeTabs counts={counts} value={sizeFilter} onChange={setSizeFilter} />
						<div className="flex flex-col gap-4">
							{visibleConfigurations.map((config) => (
								<div
									key={config.configuration}
									className={`flex flex-col gap-4 rounded-[14px] border bg-background p-6 shadow-md ${isHubOnline ? "" : "opacity-50"}`}
								>
									<div className="flex items-start justify-between gap-2">
										<div className="flex min-w-0 flex-col gap-1">
											<p className="text-xl font-breit font-medium leading-none">{formatConfigurationLabel(config.configuration)}</p>
											<p className="text-base text-muted-foreground">{formatDimensions(config.dimensions)}</p>
										</div>
										<Badge className="shrink-0">{i18n.LOCK_RENT_AVAILABLE({ count: Math.min(config.numSpaces, bookable) })}</Badge>
									</div>
									<Button
										className="w-full"
										disabled={!isHubOnline || !config.spaces[0]}
										onClick={() => deliverInto(config.spaces, selectedRequest)}
									>
										<T>{i18n.WA_SELECT_CHOOSE()}</T>
									</Button>
								</div>
							))}
						</div>
					</>
				)}
			</div>
			{hubGate.drawer}
		</PageContainer>
	);
}

type DeliveryRequest = warenannahmeDeliverQuery["response"]["deliveryRequests"]["nodes"][number];
